import type { ReactNode } from 'react';
import { Button } from '@fluentui/react-components';
import {
  ArrowClockwise24Regular,
  CloudDismiss24Regular,
  ErrorCircle24Regular,
  LockClosed24Regular,
  PlugDisconnected24Regular,
} from '@fluentui/react-icons';
import { ERROR_CODES } from '@adco-rooms/shared';
import { cn } from '@/lib/cn';
import { isApiError } from '@/lib/api/client';

/**
 * Error state.
 *
 * The variant is normally derived from the thrown `ApiError` so the user reads
 * an explanation, not a stack trace. The correlation id is shown discreetly to
 * make support requests actionable.
 */

export type ErrorStateVariant = 'connection' | 'graph' | 'forbidden' | 'not-found' | 'generic';

export interface ErrorStateProps {
  variant?: ErrorStateVariant;
  title?: string;
  description?: ReactNode;
  /** The original rejection; used to infer the variant and show its id. */
  error?: unknown;
  onRetry?: () => void;
  retryLabel?: string;
  compact?: boolean;
  className?: string;
  children?: ReactNode;
}

interface VariantPreset {
  icon: JSX.Element;
  title: string;
  description: string;
  tone: 'danger' | 'warning';
}

const PRESETS: Record<ErrorStateVariant, VariantPreset> = {
  connection: {
    icon: <PlugDisconnected24Regular />,
    title: 'No pudimos conectarnos',
    description:
      'Revisa tu conexión a internet. Los datos se recargarán automáticamente al restablecerse.',
    tone: 'warning',
  },
  graph: {
    icon: <CloudDismiss24Regular />,
    title: 'Microsoft 365 no está disponible',
    description:
      'La disponibilidad en tiempo real de Outlook no responde. Puedes seguir consultando la información local mientras se restablece el servicio.',
    tone: 'warning',
  },
  forbidden: {
    icon: <LockClosed24Regular />,
    title: 'No tienes acceso a esta sección',
    description:
      'Tu cuenta no cuenta con los permisos necesarios. Solicita acceso a un administrador de salas.',
    tone: 'danger',
  },
  'not-found': {
    icon: <ErrorCircle24Regular />,
    title: 'No encontramos lo que buscas',
    description: 'El recurso fue movido, archivado o nunca existió.',
    tone: 'warning',
  },
  generic: {
    icon: <ErrorCircle24Regular />,
    title: 'Algo salió mal',
    description: 'Ocurrió un error inesperado. Inténtalo de nuevo en unos segundos.',
    tone: 'danger',
  },
};

/** Maps a thrown value to the most helpful error variant. */
export function errorStateVariant(error: unknown): ErrorStateVariant {
  if (!isApiError(error)) return 'generic';
  if (error.isNetworkError) return 'connection';
  if (error.code === ERROR_CODES.GRAPH_UNAVAILABLE) return 'graph';
  if (error.isForbidden) return 'forbidden';
  if (error.code === ERROR_CODES.NOT_FOUND) return 'not-found';
  return 'generic';
}

export function ErrorState({
  variant,
  title,
  description,
  error,
  onRetry,
  retryLabel = 'Reintentar',
  compact = false,
  className,
  children,
}: ErrorStateProps): JSX.Element {
  const resolved = variant ?? errorStateVariant(error);
  const preset = PRESETS[resolved];
  const correlationId = isApiError(error) ? error.correlationId : undefined;
  const apiMessage = isApiError(error) ? error.message : undefined;

  return (
    <div
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center rounded-xl border bg-card text-center',
        preset.tone === 'danger'
          ? 'border-danger-200 dark:border-danger-700'
          : 'border-warning-200 dark:border-warning-700',
        compact ? 'gap-2 px-4 py-6' : 'gap-3 px-6 py-12',
        'animate-fade-in',
        className,
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'grid place-items-center rounded-pill',
          compact ? 'h-10 w-10' : 'h-14 w-14',
          preset.tone === 'danger'
            ? 'bg-danger-50 text-danger-600 dark:bg-danger-700/25'
            : 'bg-warning-50 text-warning-600 dark:bg-warning-700/25',
        )}
      >
        {preset.icon}
      </span>

      <div className="space-y-1">
        <p className={cn('font-semibold text-ink', compact ? 'text-body' : 'text-body-lg')}>
          {title ?? preset.title}
        </p>
        <p className="mx-auto max-w-md text-body text-ink-muted text-balance">
          {description ?? apiMessage ?? preset.description}
        </p>
      </div>

      {children}

      {onRetry ? (
        <Button appearance="secondary" icon={<ArrowClockwise24Regular />} onClick={onRetry}>
          {retryLabel}
        </Button>
      ) : null}

      {correlationId ? (
        <p className="pt-1 font-mono text-caption text-ink-muted">
          Id de seguimiento: <span className="select-all">{correlationId}</span>
        </p>
      ) : null}
    </div>
  );
}
